import React, { useEffect, useState } from 'react'
import { DatePicker, Spin, Tabs } from 'antd'
import { Envelope, MapPin, Phone, User, Baby, IdentificationCard } from '@phosphor-icons/react'
import CustomModal from 'components/global/CustomModal'
import CustomAvatar from 'components/global/CustomAvatar'
import { leagueEventStore } from 'state/league/leagueEvent'
import {
  useGetLeagueRollCallHistory,
  useGetLeagueParticipantHistory,
  RollCallHistoryItem,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { LeaguePlayerParticipant } from 'types/league/leagueManage'
import { ParticipantType } from './ManagePlayersModal'
import rollCallIcon from 'assets/Images/lookout.svg'
import { convertToUtc } from 'helper/utility'
import moment from 'moment'

interface Props {
  player: LeaguePlayerParticipant | null
  participantType?: ParticipantType
  onClose: () => void
}

const TYPE_CONFIG: Record<ParticipantType, { label: string; color: string; bg: string; border: string }> = {
  kid: { label: 'Kid', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  guardian: { label: 'Guardian', color: '#2BA7CA', bg: '#F0F9FF', border: '#B8E8F5' },
  authorizedAdult: { label: 'Authorized Adult', color: '#9333ea', bg: '#faf5ff', border: '#e9d5ff' },
}

const GENDER_COLOR: Record<string, { color: string; bg: string }> = {
  male: { color: '#1d4ed8', bg: '#eff6ff' },
  female: { color: '#be185d', bg: '#fdf2f8' },
}

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

/* ── Info card: label above, value below ── */
const InfoCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
}> = ({ icon, label, value }) => (
  <div
    style={{
      background: '#FAFAFA',
      border: '1px solid #EBEDF1',
      borderRadius: 10,
      padding: '12px 14px',
    }}
  >
    <div className="flex align-center gap-6px mb-6px" style={{ color: '#908F98' }}>
      {icon}
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        {label}
      </span>
    </div>
    <p style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', textTransform: 'capitalize', margin: 0 }}>
      {value}
    </p>
  </div>
)

/* ── Section divider ── */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 22 }}>
    <div className="flex align-center gap-10px mb-12px">
      <span style={{ fontSize: 11, fontWeight: 700, color: '#908F98', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
        {title}
      </span>
      <div style={{ flex: 1, height: 1, background: '#EBEDF1' }} />
    </div>
    {children}
  </div>
)

/* ── Roll-call history tab ── */
const PlayerRollCallHistory: React.FC<{ familyId: string; leagueId: string }> = ({
  familyId,
  leagueId,
}) => {
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment())
  const dateStr = selectedDate.clone().startOf('day').toISOString()
  const { data, isLoading } = useGetLeagueRollCallHistory({ leagueId, date: dateStr, familyId })
  const rollCalls: RollCallHistoryItem[] = data || []

  return (
    <div style={{ paddingTop: 16 }}>
      <div className="flex align-center gap-10px mb-16px">
        <span style={{ fontSize: 13, fontWeight: 500, color: '#444', whiteSpace: 'nowrap' }}>Select date</span>
        <DatePicker
          value={selectedDate}
          onChange={val => val && setSelectedDate(val)}
          allowClear={false}
          className="selector"
          style={{ width: 170 }}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-30px">
          <Spin />
        </div>
      ) : rollCalls.length === 0 ? (
        <div className="flex flex-column align-center py-30px" style={{ color: '#C0BFC8' }}>
          <img src={rollCallIcon} alt="" style={{ width: 40, opacity: 0.4, marginBottom: 10 }} />
          <p style={{ fontSize: 13 }}>No roll-calls found for this date</p>
        </div>
      ) : (
        <div className="flex flex-column gap-8px">
          {rollCalls.map(rc => (
            <div
              key={rc._id}
              className="flex align-center gap-12px"
              style={{
                background: '#FAFAFA',
                border: '1px solid #EBEDF1',
                borderRadius: 10,
                padding: '12px 14px',
              }}
            >
              <img src={rollCallIcon} alt="roll-call" style={{ width: 22, flexShrink: 0, opacity: 0.7 }} />
              <div>
                <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{rc.name}</p>
                <p style={{ fontSize: 12, color: '#908F98', marginTop: 2 }}>{fmtTime(rc.time)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Check-in history tab ── */
const PlayerCheckInHistory: React.FC<{ playerId: string; leagueId: string }> = ({
  playerId,
  leagueId,
}) => {
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment())
  const dateStr = convertToUtc(selectedDate.toDate())
  const { data: history, isLoading, isFetching, refetch } = useGetLeagueParticipantHistory(
    playerId,
    { leagueId, date: dateStr },
  )

  useEffect(() => {
    if (playerId && leagueId) refetch()
  }, [playerId, dateStr])

  const records: any[] = Array.isArray(history) ? history : []

  return (
    <div style={{ paddingTop: 16 }}>
      <div className="flex align-center gap-10px mb-16px">
        <span style={{ fontSize: 13, fontWeight: 500, color: '#444', whiteSpace: 'nowrap' }}>Select date</span>
        <DatePicker
          value={selectedDate}
          onChange={val => val && setSelectedDate(val)}
          allowClear={false}
          className="selector"
          style={{ width: 170 }}
        />
      </div>

      {isLoading || isFetching ? (
        <div className="flex justify-center py-30px">
          <Spin />
        </div>
      ) : records.length === 0 ? (
        <div className="flex flex-column align-center py-30px" style={{ color: '#C0BFC8' }}>
          <span style={{ fontSize: 32, marginBottom: 8 }}>📋</span>
          <p style={{ fontSize: 13 }}>No check-in history for this date</p>
        </div>
      ) : (
        <div className="flex flex-column gap-8px">
          {records.map((record: any) => (
            <div
              key={record._id}
              style={{
                background: '#FAFAFA',
                border: '1px solid #EBEDF1',
                borderRadius: 10,
                padding: '14px 16px',
              }}
            >
              {(record.checkInStatus === 'checked-in' || record.checkInStatus === 'droped-in') &&
                record.checkedInAt && (
                  <div>
                    <div className="flex align-center justify-between mb-6px">
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#16a34a',
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                        }}
                      >
                        ● Checked In
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>
                        {moment(record.checkedInAt).local().format('hh:mm A')}
                      </span>
                    </div>
                    {(record.checkInAdmin || record.checkInOrganizer) && (
                      <p style={{ fontSize: 12, color: '#908F98', marginTop: 2 }}>
                        By:{' '}
                        {record.checkInAdmin
                          ? `${record.checkInAdmin.firstName} ${record.checkInAdmin.lastName} (Admin)`
                          : `${record.checkInOrganizer.firstName} ${record.checkInOrganizer.lastName} (Organizer)`}
                      </p>
                    )}
                    {record.checkInNotes && (
                      <p style={{ fontSize: 12, color: '#908F98', marginTop: 4, fontStyle: 'italic' }}>
                        "{record.checkInNotes}"
                      </p>
                    )}
                  </div>
                )}

              {record.checkedOutAt && (
                <div
                  style={
                    record.checkedInAt
                      ? { marginTop: 10, paddingTop: 10, borderTop: '1px solid #EBEDF1' }
                      : {}
                  }
                >
                  <div className="flex align-center justify-between mb-6px">
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#ef4444',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                      }}
                    >
                      ● Checked Out
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                      {moment(record.checkedOutAt).local().format('hh:mm A')}
                    </span>
                  </div>
                  {record.checkOutAdmin && (
                    <p style={{ fontSize: 12, color: '#908F98', marginTop: 2 }}>
                      By: {record.checkOutAdmin.firstName} {record.checkOutAdmin.lastName} (Admin)
                    </p>
                  )}
                  {record.signatureImageId && (
                    <p style={{ fontSize: 12, color: '#2BA7CA', marginTop: 4 }}>✓ Signature on file</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Main modal ── */
const PlayerProfileModal: React.FC<Props> = ({ player, participantType, onClose }) => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId: string = (leagueData as any)?._id || ''

  if (!player) return null

  const fullName = `${player.firstName || ''} ${player.lastName || ''}`.trim() || '—'
  const displayName = player.nickName ? `${fullName} "${player.nickName}"` : fullName
  const age = player.dob ? moment().diff(moment(player.dob), 'years') : null
  const typeConfig = participantType ? TYPE_CONFIG[participantType] : null
  const genderStyle = player.gender ? GENDER_COLOR[player.gender.toLowerCase()] : null

  const addressLine = [
    player.address?.street,
    player.address?.city,
    player.address?.state,
    player.address?.zip,
  ]
    .filter(Boolean)
    .join(', ')

  const personalFields = [
    age !== null && { icon: <Baby size={13} />, label: 'Age', value: `${age} years old` },
    player.gender && { icon: <User size={13} />, label: 'Gender', value: player.gender },
    player.dob && { icon: <User size={13} />, label: 'Date of Birth', value: moment(player.dob).format('MMM D, YYYY') },
    player.relation && { icon: <IdentificationCard size={13} />, label: 'Relation', value: player.relation },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; value: string }[]

  const contactFields = [
    player.email && { icon: <Envelope size={13} />, label: 'Email', value: player.email },
    player.phone && { icon: <Phone size={13} />, label: 'Phone', value: player.phone },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; value: string }[]

  const historyTabs = [
    player.familyId && {
      key: 'rollcall',
      label: 'Roll-call History',
      children: <PlayerRollCallHistory familyId={player.familyId} leagueId={leagueId} />,
    },
    participantType === 'kid' && {
      key: 'checkin',
      label: 'Check-in History',
      children: <PlayerCheckInHistory playerId={player._id} leagueId={leagueId} />,
    },
  ].filter(Boolean) as { key: string; label: string; children: React.ReactNode }[]

  return (
    <CustomModal
      openModal={!!player}
      isCloseModalCustom
      closeModal={onClose}
      title="Player Profile"
      width={520}
      footer={false}
    >
      {/* ── Profile header ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f9fc 100%)',
          border: '1px solid #E8EDF5',
          borderRadius: 14,
          padding: '20px 24px',
          marginBottom: 4,
        }}
      >
        <CustomAvatar
          size={80}
          src={player.photo?.original || player.photo?.thumb}
          name={fullName}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: '#1a1a2e',
              margin: 0,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayName}
          </h3>
          <div className="flex align-center gap-8px" style={{ marginTop: 10, flexWrap: 'wrap' }}>
            {typeConfig && (
              <span
                style={{
                  background: typeConfig.bg,
                  color: typeConfig.color,
                  border: `1px solid ${typeConfig.border}`,
                  borderRadius: 20,
                  padding: '3px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {typeConfig.label}
              </span>
            )}
            {genderStyle && player.gender && (
              <span
                style={{
                  background: genderStyle.bg,
                  color: genderStyle.color,
                  border: `1px solid ${genderStyle.color}30`,
                  borderRadius: 20,
                  padding: '3px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              >
                {player.gender}
              </span>
            )}
            {age !== null && (
              <span style={{ fontSize: 13, color: '#908F98', fontWeight: 500 }}>
                {age} yrs old
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Personal information ── */}
      {personalFields.length > 0 && (
        <Section title="Personal Information">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 10,
            }}
          >
            {personalFields.map(f => (
              <InfoCard key={f.label} icon={f.icon} label={f.label} value={f.value} />
            ))}
          </div>
        </Section>
      )}

      {/* ── Contact information ── */}
      {contactFields.length > 0 && (
        <Section title="Contact Information">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 10,
            }}
          >
            {contactFields.map(f => (
              <InfoCard key={f.label} icon={f.icon} label={f.label} value={f.value} />
            ))}
          </div>
        </Section>
      )}

      {/* ── Address ── */}
      {addressLine && (
        <Section title="Address">
          <div
            style={{
              background: '#FAFAFA',
              border: '1px solid #EBEDF1',
              borderRadius: 10,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <MapPin size={16} color="#908F98" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: '#1a1a2e' }}>{addressLine}</span>
          </div>
        </Section>
      )}

      {/* ── History tabs ── */}
      {historyTabs.length > 0 && (
        <Section title="History">
          <Tabs
            items={historyTabs}
            size="small"
            style={{ marginTop: -4 }}
          />
        </Section>
      )}
    </CustomModal>
  )
}

export default PlayerProfileModal
